import {
  BackButton,
  Card,
  ErrorAlert,
  LoadingAlert,
  Text,
  WalletFoundButNoAssetsFoundToDeployWarningAlert,
} from '@/components';
import {
  useFormData,
  useYieldBearingAssetRawData,
  useYieldBearingAssets,
} from '@/components/providers';
import { useNavigation } from '@/components/providers/common/NavigationProvider';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import AnimatedLayout from '@/components/templates/AnimatedLayout';
import APP_CONFIG from '@/config/app';
import {
  UnderlyingYieldBearingAssetInterface,
  WalletTokenInterface,
  YieldBearingAssetInterface,
} from '@/interfaces';
import { calculateOptimalPortfolioForRiskFromMonthlyApyAndTvlData } from '@/utils/functionalUtils';
import { logInfo } from '@/utils/logger';
import {
  LABELS,
  MESSAGES,
  WALLET,
  YIELD_BEARING_ASSET_ACTIONS_CARD,
} from '@/utils/strings';
import {
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  YieldBearingAssetDepositFormDataInterface,
  YieldBearingAssetInvestmentActionsCardProps,
} from './interfaces/YieldBearingAssetActionsInterfaces';
import YieldBearingAssetInvestmentAllocationConfiguration from './YieldBearingAssetInvestmentAllocationConfiguration';
import YieldBearingAssetInvestmentDepositConfirmationReceipt from './YieldBearingAssetInvestmentDepositConfirmationReceipt';
import YieldBearingAssetInvestmentDepositConfirmationRequest from './YieldBearingAssetInvestmentDepositConfirmationRequest';
import YieldBearingAssetInvestmentDepositForm from './YieldBearingAssetInvestmentDepositForm';
const { BIG_HEADING, BIG_HEADING_COLOR } = APP_CONFIG.STYLES.TEXT;
const YieldBearingAssetInvestmentActionsCard: FC<
  YieldBearingAssetInvestmentActionsCardProps
> = ({ ticker, className }) => {
  const { optimizePortfolio } = useYieldBearingAssets();
  const { getAssetYieldData } = useYieldBearingAssetRawData();
  const { currentScreen, navigateBack, canNavigateBack, direction } =
    useNavigation();
  const {
    tokensFound,
    tokens,
    getEligibleTokensForDeposit,
    isInitialLoading: userPortfolioAssetsInitialLoading,
    error: userPortfolioAssetsError,
    isUserAuthorized,
  } = useWalletsContext();
  const {
    yieldBearingAssetsFound,
    yieldBearingAssets,
    initialLoading: yieldBearingAssetsInitialLoading,
    error: yieldBearingAssetsError,
  } = useYieldBearingAssets();
  const { updateFormData } =
    useFormData<YieldBearingAssetDepositFormDataInterface>('depositForm');
  const [yieldBearingAsset, setYieldBearingAsset] =
    useState<YieldBearingAssetInterface | null>(null);
  const [depositData, setDepositData] =
    useState<YieldBearingAssetDepositFormDataInterface>({
      amount: 0,
      token: {} as WalletTokenInterface,
      underlyingYieldBearingAssetsAllocation: [],
    });
  const [projectionData, setProjectionData] = useState<
    { value: number; time: string; label: string }[]
  >([]);
  const [riskProfile, setRiskProfile] = useState(0);
  const initialized = useRef(false);
  const [isOptimizing, setIsOptimizing] = useState<boolean>(false);
  const [optimizationExplanation, setOptimizationExplanation] =
    useState<string>('');
  const getFilteredEligibleTokensForAsset = useCallback(
    (asset: YieldBearingAssetInterface | null) => {
      const eligibleTokens = getEligibleTokensForDeposit();
      if (!asset) {
        return eligibleTokens;
      }

      if (
        Array.isArray(asset.tokensSupportedForDeposit) &&
        asset.tokensSupportedForDeposit.length > 0
      ) {
        const supportedTokenIds = new Set(
          asset.tokensSupportedForDeposit
            .map((token) => token.id)
            .filter(Boolean),
        );
        return eligibleTokens.filter((token) => supportedTokenIds.has(token.id));
      }

      return eligibleTokens.filter((token) => {
        const isExcluded =
          APP_CONFIG.BLOCKCHAINS_NOT_SUPPORTED_FOR_YIELD_BEARING_ASSET_DEPLOYMENT.includes(
            token.blockchain,
          );
        if (isExcluded) {
          return false;
        }
        if (!APP_CONFIG.ALLOW_CROSS_CHAIN_YIELD_DEPLOYMENTS) {
          return asset.underlyingYieldBearingAssets.some(
            (underlyingAsset) => underlyingAsset.blockchain === token.blockchain,
          );
        }
        return true;
      });
    },
    [getEligibleTokensForDeposit],
  );
  const handleOptimize = async (
    assessment: { question: string; rating: number }[],
    userCommentary?: string,
  ) => {
    setIsOptimizing(true);
    if (!yieldBearingAsset?.underlyingYieldBearingAssets?.length) {
      console.warn('> No underlying assets found; skipping optimization.');
      setIsOptimizing(false);
      return;
    }
    let assetsWithData;
    try {
      assetsWithData = await Promise.all(
        yieldBearingAsset.underlyingYieldBearingAssets.map(async (asset) => {
          const yieldData = await getAssetYieldData(
            asset.defiLlamaId,
            false,
            'monthly',
          );
          const validTimeSeriesData = (yieldData?.timeSeriesData || []).map(
            (d) => ({
              timestamp: d.timestamp,
              apy: d.apy || 0,
              tvlUsd: d.tvlUsd || d.tvlUSD || 0,
            }),
          );
          return { name: asset.name, timeSeriesData: validTimeSeriesData };
        }),
      );
    } catch (fetchError) {
      console.error('> Failed to fetch underlying asset data:', fetchError);
      setIsOptimizing(false);
      return;
    }
    try {
      const result = await optimizePortfolio(
        assetsWithData,
        assessment,
        userCommentary,
      );
      setOptimizationExplanation(result.explanation);
      const newAllocation = yieldBearingAsset.underlyingYieldBearingAssets.map(
        (asset, index) => ({
          ...asset,
          composition: result.allocations[index] || 0,
        }),
      );
      handleDepositDataChange((prev) => ({
        ...prev,
        underlyingYieldBearingAssetsAllocation: newAllocation,
        userCommentary: userCommentary,
      }));
    } catch (apiError) {
      console.warn('> API request failed; using fallback:', apiError);
      try {
        const sumRatings = assessment.reduce(
          (acc, item) => acc + item.rating,
          0,
        );
        const maxScore = assessment.length * 5;
        const riskTolerance = maxScore === 0 ? 0 : sumRatings / maxScore;
        const fallbackAllocations =
          calculateOptimalPortfolioForRiskFromMonthlyApyAndTvlData(
            assetsWithData,
            riskTolerance,
          );
        const newAllocation =
          yieldBearingAsset.underlyingYieldBearingAssets.map(
            (asset, index) => ({
              ...asset,
              composition: fallbackAllocations[index],
            }),
          );
        handleDepositDataChange((prev) => ({
          ...prev,
          underlyingYieldBearingAssetsAllocation: newAllocation,
          userCommentary: userCommentary,
        }));
      } catch (fallbackError) {
        console.error('> Fallback method failed:', fallbackError);
      }
    } finally {
      setIsOptimizing(false);
    }
  };
  useEffect(() => {
    if (
      !yieldBearingAssetsInitialLoading &&
      !userPortfolioAssetsInitialLoading &&
      yieldBearingAssetsFound &&
      tokensFound &&
      !initialized.current
    ) {
      const found = yieldBearingAssets.find((a) => a.ticker === ticker) || null;
      setYieldBearingAsset(found);
      const eligibleTokens = getEligibleTokensForDeposit();
      const filteredTokens = getFilteredEligibleTokensForAsset(found);
      console.log(
        '[DEBUG] YieldBearingAssetInvestmentActionsCard initialization:',
        {
          ticker,
          found,
          foundBlockchains: found?.underlyingYieldBearingAssets?.map(
            (a) => a.blockchain,
          ),
          allTokens: tokens,
          allTokensCount: tokens.length,
          eligibleTokens,
          eligibleTokensCount: eligibleTokens.length,
          filteredTokens,
          filteredTokensCount: filteredTokens.length,
          filteredTokensBlockchains: filteredTokens.map((t) => ({
            symbol: t.symbol,
            blockchain: t.blockchain,
          })),
        },
      );
      const defaultToken = filteredTokens[0] || ({} as WalletTokenInterface);
      setDepositData({
        amount:
          (defaultToken.balance || 0) *
          APP_CONFIG.ASSET_TO_SELL_DEFAULT_PERCENTAGE,
        token: defaultToken,
        underlyingYieldBearingAssetsAllocation: [],
      });
      navigateBack('deposit-initial');
      initialized.current = true;
    }
  }, [
    yieldBearingAssetsInitialLoading,
    userPortfolioAssetsInitialLoading,
    yieldBearingAssetsFound,
    tokensFound,
    yieldBearingAssets,
    tokens,
    ticker,
    navigateBack,
    getEligibleTokensForDeposit,
    getFilteredEligibleTokensForAsset,
  ]);
  const handleDepositDataChange = (
    data: SetStateAction<YieldBearingAssetDepositFormDataInterface>,
  ) => {
    setDepositData((prev) => {
      const nextData = typeof data === 'function' ? data(prev) : data;
      return {
        ...prev,
        ...nextData,
        underlyingYieldBearingAssetsAllocation:
          nextData.underlyingYieldBearingAssetsAllocation ??
          prev.underlyingYieldBearingAssetsAllocation,
      };
    });
  };
  const handleCompositionConfirm = (
    confirmedAssets: UnderlyingYieldBearingAssetInterface[],
  ) => {
    setDepositData((prev) => {
      const updatedData = {
        ...prev,
        underlyingYieldBearingAssetsAllocation: confirmedAssets,
      };
      updateFormData(updatedData);
      return updatedData;
    });
    navigateBack();
  };
  const handleProjectionDataUpdate = (
    newData: { value: number; time: string; label: string }[],
  ) => {
    setProjectionData(newData);
  };
  const resetDepositData = () => {
    if (tokensFound && yieldBearingAsset) {
      const filteredTokens = getFilteredEligibleTokensForAsset(yieldBearingAsset);
      const defaultToken = filteredTokens[0] || ({} as WalletTokenInterface);
      setDepositData({
        amount:
          (defaultToken.balance || 0) *
          APP_CONFIG.ASSET_TO_SELL_DEFAULT_PERCENTAGE,
        token: defaultToken,
        underlyingYieldBearingAssetsAllocation: [],
      });
    } else {
      setDepositData({
        amount: 0,
        token: {} as WalletTokenInterface,
        underlyingYieldBearingAssetsAllocation: [],
      });
    }
  };
  const getHeaderLeft = () => {
    if (currentScreen === 'deposit-initial') {
      return (
        <Text
          size={BIG_HEADING}
          color={BIG_HEADING_COLOR}
          value={
            LABELS.YIELD_BEARING_ASSET_INVESTMENT_ACTIONS_CARD.INVEST_SCREEN
              .HEADER
          }
        />
      );
    }
    return canNavigateBack ? (
      <BackButton onBackClick={() => navigateBack()} />
    ) : null;
  };
  const getHeaderCenter = () => {
    switch (currentScreen) {
      case 'allocation-configuration':
        return (
          <Text
            size={BIG_HEADING}
            color={BIG_HEADING_COLOR}
            value={
              LABELS.YIELD_BEARING_ASSET_INVESTMENT_ACTIONS_CARD
                .ALLOCATION_CONFIGURATION_SCREEN.SCREEN_HEADER
            }
          />
        );
      case 'deposit-confirmation':
        return (
          <Text
            size={BIG_HEADING}
            color={BIG_HEADING_COLOR}
            value={
              LABELS.YIELD_BEARING_ASSET_INVESTMENT_ACTIONS_CARD
                .CONFIRMATION_REQUEST_SCREEN.DEPOSIT.SCREEN_HEADER
            }
          />
        );
      case 'deposit-final':
        return (
          <Text
            size={BIG_HEADING}
            color={BIG_HEADING_COLOR}
            value={
              LABELS.YIELD_BEARING_ASSET_INVESTMENT_ACTIONS_CARD
                .CONFIRMATION_RECEIPT_SCREEN.DEPOSIT.SCREEN_HEADER
            }
          />
        );
      default:
        return null;
    }
  };
  useEffect(() => {
    logInfo(
      `isUserAuthorized: ${isUserAuthorized}`,
      'YieldBearingAssetInvestmentActionsCard',
    );
  }, [isUserAuthorized]);
  const renderCardBody = useMemo(() => {
    const initialLoading =
      yieldBearingAssetsInitialLoading || userPortfolioAssetsInitialLoading;
    const error = yieldBearingAssetsError || userPortfolioAssetsError;
    if (!isUserAuthorized) {
      return (
        <ErrorAlert
          title={WALLET.UNAUTHORIZED_USER_ERROR_TITLE}
          description={WALLET.UNAUTHORIZED_USER_ERROR_DESCRIPTION}
        />
      );
    }
    if (initialLoading) {
      return (
        <LoadingAlert
          title={YIELD_BEARING_ASSET_ACTIONS_CARD.LOADING_TITLE}
          description={YIELD_BEARING_ASSET_ACTIONS_CARD.LOADING_DESCRIPTION}
        />
      );
    }
    if (error) {
      return (
        <ErrorAlert
          title={MESSAGES.YIELD_BEARING_ASSETS.ERROR_LOADING_TITLE}
          description={MESSAGES.YIELD_BEARING_ASSETS.ERROR_LOADING_DESCRIPTION}
        />
      );
    }
    if (!tokensFound) {
      return <WalletFoundButNoAssetsFoundToDeployWarningAlert />;
    }
    if (!yieldBearingAsset) {
      return (
        <ErrorAlert
          title={MESSAGES.YIELD_BEARING_ASSET_DETAIL.ERROR_LOADING_TITLE.replace(
            '[asset_ticker]',
            ticker,
          )}
          description={MESSAGES.YIELD_BEARING_ASSET_DETAIL.ERROR_LOADING_DESCRIPTION.replace(
            '[asset_ticker]',
            ticker,
          )}
        />
      );
    }
    switch (currentScreen) {
      case 'deposit-initial':
        return (
          <YieldBearingAssetInvestmentDepositForm
            yieldBearingAsset={yieldBearingAsset}
            setProjectionData={handleProjectionDataUpdate}
            projectionData={projectionData}
            setDepositData={handleDepositDataChange}
            depositData={depositData}
            showHistoricalPerformance={false}
          />
        );
      case 'allocation-configuration':
        return (
          <YieldBearingAssetInvestmentAllocationConfiguration
            yieldBearingAsset={yieldBearingAsset}
            handleCompositionConfirm={handleCompositionConfirm}
            depositData={depositData}
            onOptimize={handleOptimize}
            isOptimizing={isOptimizing}
            optimizationExplanation={optimizationExplanation}
          />
        );
      case 'deposit-confirmation':
        return (
          <YieldBearingAssetInvestmentDepositConfirmationRequest
            yieldBearingAsset={yieldBearingAsset}
            depositData={depositData}
          />
        );
      case 'deposit-final':
        return (
          <YieldBearingAssetInvestmentDepositConfirmationReceipt
            yieldBearingAsset={yieldBearingAsset}
            depositData={depositData}
            confirmationReceiptDoneButtonClickHandler={resetDepositData}
          />
        );
      default:
        return null;
    }
  }, [
    currentScreen,
    depositData,
    projectionData,
    yieldBearingAsset,
    yieldBearingAssetsInitialLoading,
    userPortfolioAssetsInitialLoading,
    yieldBearingAssetsError,
    userPortfolioAssetsError,
    isUserAuthorized,
    isOptimizing,
    tokensFound,
    ticker,
  ]);
  return (
    <Card className={className}>
      <Card.Header left={getHeaderLeft()} center={getHeaderCenter()} />
      <Card.Body>
        <AnimatedLayout uniqueKey={currentScreen} slideFrom={direction}>
          {renderCardBody}
        </AnimatedLayout>
      </Card.Body>
    </Card>
  );
};
export default YieldBearingAssetInvestmentActionsCard;
